import { useRef, type FormEvent } from "react";
import { BF001_DEV_OTP } from "../flows/buyerFunding/deviceVerification";
import { FlowStepPhoneFrame } from "./FlowStepPhoneFrame";

const OTP_LENGTH = 6;

type Props = {
  maskedPhone: string;
  otp: string;
  otpError: string;
  devMode?: boolean;
  sending?: boolean;
  onOtpChange: (value: string) => void;
  onSubmit: (event: FormEvent) => void;
  onResend?: () => void;
};

export function BuyerDeviceValidationStep({
  maskedPhone,
  otp,
  otpError,
  devMode = false,
  sending = false,
  onOtpChange,
  onSubmit,
  onResend
}: Props) {
  const hiddenRef = useRef<HTMLInputElement>(null);
  const digits = otp.padEnd(OTP_LENGTH, " ").slice(0, OTP_LENGTH).split("");

  function focusInput() {
    hiddenRef.current?.focus();
  }

  return (
    <FlowStepPhoneFrame testId="buyer-device-screen">
          <p className="text-center text-lg font-semibold text-[#191919]">{maskedPhone}</p>

          <form className="mt-4" onSubmit={onSubmit}>
            <div
              className="relative flex w-full justify-center gap-1.5 sm:gap-2"
              role="group"
              aria-label="Security code"
              onClick={focusInput}
            >
              <input
                ref={hiddenRef}
                data-testid="buyer-device-otp"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                value={otp}
                onChange={(e) => onOtpChange(e.target.value.replace(/\D/g, "").slice(0, OTP_LENGTH))}
                className="absolute inset-0 cursor-text opacity-0"
                aria-label="Six digit security code"
              />
              {digits.map((digit, index) => (
                <div
                  key={index}
                  className={`flex h-12 w-9 shrink-0 items-center justify-center rounded-[10px] border-2 text-lg font-bold sm:w-10 ${
                    index === otp.length
                      ? "border-[#1f6b57] bg-[#e6f0eb] text-[#1f6b57]"
                      : digit.trim()
                        ? "border-[#1f6b57] bg-[#f7fbf9] text-[#191919]"
                        : "border-[#d8d2c4] bg-white text-[#596158]"
                  }`}
                >
                  {digit.trim() ? "•" : ""}
                </div>
              ))}
            </div>

            {devMode ? (
              <p className="mt-3 text-center text-[11px] text-[#8a8f87]">Demo code: {BF001_DEV_OTP}</p>
            ) : (
              <p className="mt-3 text-center text-[11px] text-[#8a8f87]">
                {sending ? "Sending code…" : "Check your phone for a text message from Veto."}
              </p>
            )}

            {onResend ? (
              <button
                className="mt-3 w-full text-center text-xs font-semibold text-[#1f6b57] hover:underline disabled:opacity-40"
                data-testid="buyer-device-resend"
                type="button"
                disabled={sending}
                onClick={onResend}
              >
                Resend code
              </button>
            ) : null}

            <button
              className="button-primary mt-6 w-full"
              data-testid="buyer-device-submit"
              type="submit"
              disabled={sending || otp.replace(/\D/g, "").length < OTP_LENGTH}
            >
              Submit
            </button>
          </form>

          {otpError ? <p className="mt-3 text-center text-sm text-[#8a2d10]">{otpError}</p> : null}
    </FlowStepPhoneFrame>
  );
}
